/**
 * Store for managing members data
 *
 * IMPORTANT REACTIVITY NOTES:
 * 1. Always use $patch for state updates to ensure proper reactivity
 * 2. Cross-store dependencies are managed through subscriptions —
 *    `subscribeToCurrentMember` is the live one; it reconciles captain status
 *    via `subkrewesStore.ensureCaptainSynchronized`
 * 3. `_lastSyncedType` records the member type last reconciled, so a type
 *    change can be detected without re-triggering itself
 * 4. Captain and SubKrewe relationships are maintained bidirectionally
 */
import type { DocumentReference } from 'firebase/firestore'
import {
  collection,
  deleteDoc,
  deleteField, doc, getDoc, getFirestore,
  onSnapshot,
  query, serverTimestamp, updateDoc, where,
} from 'firebase/firestore'
import { defineStore } from 'pinia'
import { useCurrentUser } from 'vuefire'
import type { LiveChange, LiveSpec } from '~/utils/liveData'
import { acquireLive, holdLive, isLiveCovered, liveDocListener, liveQueryListener } from '~/utils/liveData'
import type { CaptainProfilesResponse } from '~/shared/types/captainProfile.js'
import type { Member, MemberFirestoreData } from '~/shared/types/member.js'
// Add type guard for redshirt restrictions
import { isRedshirt } from '~/shared/types/memberType.js'
import { captainProfileToMember } from '~/shared/utils/captainProfile.js'
import { canAwardComps, canGrantAdmin } from '~/shared/utils/grantPrivileges.js'
import { deepTrim } from '~/shared/utils/sanitize.js'
import { isDirectlyJoinable } from '~/shared/utils/subkreweJoin.js'

// The SubKrewe each live member record points at (its path, or null), as
// stored: a SubKrewe that couldn't be read leaves member.subkrewe null, and
// the check-in screen must tell that apart from no SubKrewe (loadMember)
const subkrewePathOf = new Map<string, string | null>()

// The captain profiles from the server (members/{id} → profile), for those
// who can't read member records. A profile fills in for a captain only while
// no record of theirs is live, and is never mistaken for one.
const captainProfiles = new Map<string, Member>()

// The roles firestore.rules lets read every member record
function readsAllMembers(roles: Member['roles'] | undefined): boolean {
  return roles?.admin === true || roles?.band_admin === true
    || roles?.redshirt_admin === true || roles?.checkinVolunteer === true
}

// The member fields an update may write (as convertToFirestoreData lists
// them; subkrewe and purchases are converted separately)
const MEMBER_WRITABLE_FIELDS: readonly string[] = [
  'name', 'first_name', 'last_name', 'email', 'member_type', 'roles', 'address',
  'phone', 'birthday', 'displayName', 'children', 'hide_phone',
  'isRedshirtLieutenant', 'tshirt_size', 'emergency_contact', 'emergency_contacts',
  'subkrewe_terms_acceptances', 'rolling_by_year',
  // Signup's agreements: the Code of Conduct, and when the krewe's terms
  // were accepted
  'code_of_conduct_acknowledged', 'terms_accepted_at',
]

// Takes a member who is being banned or deleted out of their SubKrewe. False
// when the SubKrewe couldn't be updated, so the ban or delete stops there.
async function removeFromOwnSubKrewe(member: Member, actingUserId: string | undefined, reason: string): Promise<boolean> {
  const subkrewe = member.subkrewe
  if (!subkrewe) return true
  const subkrewesStore = useSubKrewesStore()
  if (member.member_type === 'captain') {
    // A captain the SubKrewe doesn't list has nothing to remove there
    if (!subkrewe.captains?.some(c => c.id === member.id)) return true
    return subkrewesStore.removeCaptainFromSubKrewe(subkrewe, member, false)
  }
  return subkrewesStore.removeMemberFromSubKrewe(subkrewe, member, actingUserId, reason)
}

export const useMembersStore = defineStore('members', {
  state: () => ({
    // Store members by document path for O(1) lookups
    members: {} as Record<string, Member>,
    loading: false,
  }),

  getters: {
    // Convert map to array for components that expect arrays
    membersArray(): Member[] {
      return Object.values(this.members)
    },

    // Get all captains (no need to fetch separately)
    captains(): Member[] {
      return this.membersArray.filter(m => m.member_type === 'captain')
    },

    // Get available captains (not already assigned)
    availableCaptains(): Member[] {
      return this.captains.filter(c => !c.subkrewe)
    },
  },

  actions: {
    /**
     * Convert Firestore document data to a Member object
     */
    convertToMember(docId: string, data: MemberFirestoreData, resolveSubkrewe = true): Member {
      // Handle date conversions
      const created_at = data.created_at ? data.created_at : new Date()
      const updated_at = data.updated_at ? data.updated_at : new Date()

      // Create the basic member without resolved subkrewe
      const member: Member = {
        id: docId,
        name: data.name || '',
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email || '',
        member_type: data.member_type,
        created_at,
        updated_at,
        // Initially null, will be resolved later if needed
        subkrewe: null,
        roles: data.roles,
        address: data.address,
        phone: data.phone,
        hide_phone: data.hide_phone,
        birthday: data.birthday,
        banned: data.banned,
        beta_tester: data.beta_tester,
        // Server-managed (SES event webhook); read-only on the client, so
        // these are deliberately absent from convertToFirestoreData.
        email_bounce_type: data.email_bounce_type,
        email_bounce_reason: data.email_bounce_reason,
        email_bounced_at: data.email_bounced_at,
        email_delivered_at: data.email_delivered_at,
        lifetime: data.lifetime,
        lifetime_reason: data.lifetime_reason,
        lifetime_since: data.lifetime_since,
        golden_wookiee: data.golden_wookiee,
        golden_wookiee_year: data.golden_wookiee_year,
        golden_wookiee_reason: data.golden_wookiee_reason,
        purchases: data.purchases,
        // Add this line to include children data
        children: data.children,
        // Redshirt-specific fields
        isRedshirtLieutenant: data.isRedshirtLieutenant ?? false,
        tshirt_size: data.tshirt_size,
        // Emergency contact (legacy single contact)
        emergency_contact: data.emergency_contact,
        // Emergency contacts (new array format)
        emergency_contacts: data.emergency_contacts,
        // SubKrewe-specific terms & conditions acceptances (keyed by SubKrewe id)
        subkrewe_terms_acceptances: data.subkrewe_terms_acceptances,
        // Per-parade-year rolling opt-out map
        rolling_by_year: data.rolling_by_year,
      }
      if (!data.subkrewe || !resolveSubkrewe) {
        return member
      }
      const subkrewesStore = useSubKrewesStore()
      const subkrewePath = data.subkrewe.path
      const subkrewe = subkrewesStore.subkrewes[subkrewePath]

      if (subkrewe) {
        member.subkrewe = subkrewe
      }

      return member
    },

    /**
     * Convert a Member object to Firestore-compatible data format
     */
    convertToFirestoreData(member: Member): Partial<MemberFirestoreData> {
      const db = getFirestore()

      // Create a properly typed object for Firestoreg425
      const firestoreData: Partial<MemberFirestoreData> = {
        // Basic fields
        name: member.name,
        first_name: member.first_name,
        last_name: member.last_name,
        email: member.email,
        member_type: member.member_type,
        updated_at: serverTimestamp(),

        // Optional fields
        roles: member.roles,
        address: member.address,
        phone: member.phone,
        birthday: member.birthday,
        displayName: member.displayName,
        purchases: member.purchases,
        children: member.children,
        hide_phone: member.hide_phone,
        // Redshirt fields
        isRedshirtLieutenant: member.isRedshirtLieutenant,
        tshirt_size: member.tshirt_size,
        // Emergency contact (legacy single contact)
        emergency_contact: member.emergency_contact,
        // Emergency contacts (new array format)
        emergency_contacts: member.emergency_contacts,
        // SubKrewe-specific terms & conditions acceptances (keyed by SubKrewe id)
        subkrewe_terms_acceptances: member.subkrewe_terms_acceptances,
        // Per-parade-year rolling opt-out map
        rolling_by_year: member.rolling_by_year,
      }

      // Remove any undefined fields, because Firestore doesn't like them.
      // Iterated through a Record view: the keys come from `for...in`, which
      // TypeScript widens to `string`, and so cannot index the typed shape.
      const writableFields = firestoreData as Record<string, unknown>
      for (const key in writableFields) {
        if (writableFields[key] === undefined) {
          writableFields[key] = deleteField()
        }
      }

      // Handle subkrewe reference - convert object to DocumentReference
      if (member.subkrewe) {
        firestoreData.subkrewe = doc(db, 'subkrewes', member.subkrewe.id)
      }
      else {
        firestoreData.subkrewe = deleteField()
      }

      return firestoreData
    },

    /**
     * Toggle a redshirt's Lieutenant status
     * Only meaningful for member.member_type === 'redshirt'
     */
    async toggleRedshirtLieutenant(member: Member, makeLieutenant: boolean): Promise<boolean> {
      try {
        if (!member?.id) {
          console.error('Cannot toggle lieutenant: invalid member object')
          return false
        }

        if (member.member_type !== 'redshirt') {
          console.warn(`[Members] Ignoring lieutenant toggle for non-redshirt ${member.id}`)
          return false
        }

        const db = getFirestore()
        const memberRef = doc(db, 'members', member.id)
        const memberPath = `members/${member.id}`

        await updateDoc(memberRef, {
          isRedshirtLieutenant: makeLieutenant,
          updated_at: serverTimestamp(),
        })

        this.$patch((state) => {
          if (!state.members[memberPath]) {
            state.members[memberPath] = member
          }
          state.members[memberPath].isRedshirtLieutenant = makeLieutenant
          state.members[memberPath].updated_at = new Date()
        })

        return true
      }
      catch (error) {
        console.error(`Error toggling redshirt lieutenant for member ${member.id}:`, error)
        return false
      }
    },

    /**
     * Every captain, live while the caller holds them (utils/liveData.ts).
     * Those who read every member record get the records; everyone else gets
     * captains' names (and their own captains' contact details) from the
     * server, as member records are private outside a member's SubKrewe.
     */
    async fetchCaptains(): Promise<Member[]> {
      // The roles decide which source: read them without waiting when we
      // can, so a SubKrewe being applied holds the captains it shows
      const ownRoles = this.members[`members/${useCurrentUser().value?.uid}`]?.roles
      const roles = ownRoles ?? await this.ownRoles()
      try {
        await acquireLive(readsAllMembers(roles) ? this.captainsSpec() : this.captainProfilesSpec())
      }
      catch (error) {
        console.error('Error loading captains:', error)
      }
      return this.captains
    },

    /**
     * One captain, as SubKrewes resolve them
     */
    async fetchCaptain(id: string): Promise<Member | null> {
      await this.fetchCaptains()
      const captain = this.members[`members/${id}`]
      if (captain) {
        return captain
      }
      // The caller themselves, not loaded yet, or someone who isn't a captain
      // by member_type (a stale reference), whom only those who read every
      // member record can look up
      const readable = id === useCurrentUser().value?.uid || readsAllMembers(await this.ownRoles())
      return readable ? this.fetchMember(id) : null
    },

    /**
     * The signed-in member's roles. Read from their record without resolving
     * its SubKrewe, which would wait on the captains being loaded (a one-time
     * read: it decides, it isn't stored).
     */
    async ownRoles(): Promise<Member['roles'] | undefined> {
      const uid = useCurrentUser().value?.uid
      if (!uid) {
        return undefined
      }
      const loaded = this.members[`members/${uid}`]
      if (loaded) {
        return loaded.roles
      }
      const snap = await getDoc(doc(getFirestore(), 'members', uid))
      return (snap.data() as MemberFirestoreData | undefined)?.roles
    },

    /**
     * Every red shirt (with resolved subkrewes), live while the caller holds
     * them — used by the equipment station, which must not download the
     * whole members collection
     */
    async fetchRedshirts(): Promise<Member[]> {
      try {
        await acquireLive(this.queriedMembersSpec('member_type=redshirt', where('member_type', '==', 'redshirt')))
      }
      catch (error) {
        console.error('Error loading redshirts:', error)
      }
      return this.membersArray.filter(member => member.member_type === 'redshirt')
    },

    /**
     * A member, live while the caller holds them. Null when there's no such
     * member or they couldn't be read.
     */
    async fetchMember(id: string): Promise<Member | null> {
      try {
        return await this.loadMember(id)
      }
      catch (error) {
        console.error(`Error fetching member ${id}:`, error)
        return null
      }
    },

    /**
     * A member, live while the caller holds them. Unlike fetchMember this
     * throws when the read fails, so a caller can tell "couldn't load"
     * (offline) apart from "no such member" (null).
     */
    async loadMember(id: string, opts: { strictSubkrewe?: boolean } = {}): Promise<Member | null> {
      const path = `members/${id}`
      await acquireLive(this.memberSpec(id))
      const member = this.members[path] ?? null
      if (!member) {
        return null
      }

      // A SubKrewe that couldn't be read made a member look like one without
      // a SubKrewe: the wrong dues and check-in rules. `strictSubkrewe` (the
      // check-in screen) throws then; a SubKrewe that no longer exists still
      // counts as none.
      const subkrewePath = subkrewePathOf.get(path)
      if (opts.strictSubkrewe && !member.subkrewe && subkrewePath) {
        const subkreweSnap = await getDoc(doc(getFirestore(), subkrewePath))
        if (subkreweSnap.exists()) {
          throw new Error(`Couldn't load the SubKrewe of member ${id}`)
        }
      }
      return member
    },

    /**
     * Every member, live while the caller holds them (admins hold them from
     * sign-in, stores/adminLive.ts)
     */
    async fetchMembers(): Promise<Member[]> {
      this.loading = !isLiveCovered('members')
      try {
        await acquireLive(useAdminLiveStore().membersSpec())
      }
      catch (error) {
        console.error('Error fetching members:', error)
      }
      finally {
        this.loading = false
      }
      return this.membersArray
    },

    /**
     * Members who have any of these roles (admin, redshirt_admin, etc.),
     * live while the caller holds them
     */
    async fetchMembersByRole(roles: string[]): Promise<Member[]> {
      try {
        // Firestore doesn't do OR across fields: a query per role
        await Promise.all(roles.map(role => acquireLive(this.queriedMembersSpec(`roles.${role}=true`, where(`roles.${role}`, '==', true)))))
      }
      catch (error) {
        console.error('Error fetching members by role:', error)
        return []
      }
      const has = (member: Member) => roles.some(role => (member.roles as Record<string, unknown> | undefined)?.[role] === true)
      return this.membersArray.filter(has)
    },

    /**
     * A member's SubKrewe, held for as long as whoever asked holds the member
     */
    async resolveSubkreweForMember(subkreweRef: DocumentReference | undefined | null): Promise<SubKrewe | null> {
      if (!subkreweRef) return null
      return await useSubKrewesStore().fetchSubKreweByRef(subkreweRef)
    },

    memberSpec(id: string): LiveSpec<MemberFirestoreData> {
      const path = `members/${id}`
      return {
        key: path,
        // The signed-in member's own record always gets its own listener:
        // its changes (type, SubKrewe) set things in motion (applyMembers)
        within: id === useCurrentUser().value?.uid ? undefined : 'members',
        listen: (emit, fail) => onSnapshot(doc(getFirestore(), 'members', id), ...liveDocListener(path, emit, fail)),
        apply: changes => this.applyMembers(changes),
        evict: paths => this.evictMembers(paths),
      }
    },

    queriedMembersSpec(filterKey: string, filter: ReturnType<typeof where>): LiveSpec<MemberFirestoreData> {
      return {
        key: `members?${filterKey}`,
        within: 'members',
        listen: (emit, fail) => onSnapshot(query(collection(getFirestore(), 'members'), filter), ...liveQueryListener('members', emit, fail)),
        apply: changes => this.applyMembers(changes),
        evict: paths => this.evictMembers(paths),
      }
    },

    captainsSpec(): LiveSpec<MemberFirestoreData> {
      return {
        key: 'members?member_type=captain',
        within: 'members',
        listen: (emit, fail) => onSnapshot(
          query(collection(getFirestore(), 'members'), where('member_type', '==', 'captain')),
          ...liveQueryListener('members', emit, fail),
        ),
        // Captains without their SubKrewe resolved: the SubKrewes resolve
        // their captains, and waiting on each other would deadlock
        apply: (changes) => {
          this.$patch((state) => {
            for (const { path, data } of changes) {
              subkrewePathOf.set(path, data!.subkrewe?.path ?? null)
              state.members[path] = this.convertToMember(path.slice('members/'.length), data!, false)
            }
          })
        },
        evict: paths => this.evictMembers(paths),
      }
    },

    /**
     * The captain profiles from the server: not a listener (the records they
     * come from are private), so they're read once and dropped when let go
     */
    captainProfilesSpec(): LiveSpec<Member> {
      return {
        key: 'captain-profiles',
        listen: (emit, fail) => {
          let stopped = false
          $fetch<CaptainProfilesResponse>('/api/subkrewes/captains')
            .then(({ paradeYear, captains }) => {
              if (stopped) return
              emit(captains.map(profile => ({ path: `captain-profiles/${profile.id}`, data: captainProfileToMember(profile, paradeYear) })))
            })
            .catch((error: unknown) => {
              if (!stopped) fail(error)
            })
          return () => {
            stopped = true
          }
        },
        apply: (changes) => {
          const ownId = useCurrentUser().value?.uid
          this.$patch((state) => {
            for (const { data } of changes) {
              const profile = data!
              // Never the caller's own: it would stand in for their record
              if (profile.id === ownId) continue
              const path = `members/${profile.id}`
              captainProfiles.set(path, profile)
              // A record (a SubKrewe-mate's) beats a profile
              if (!state.members[path] || state.members[path] === captainProfiles.get(path)) {
                state.members[path] = profile
              }
            }
          })
        },
        evict: (paths) => {
          this.$patch((state) => {
            for (const profilePath of paths) {
              const path = `members/${profilePath.slice('captain-profiles/'.length)}`
              const profile = captainProfiles.get(path)
              captainProfiles.delete(path)
              if (profile && state.members[path] === profile) delete state.members[path]
            }
          })
        },
      }
    },

    /**
     * Put live member records into the store, each with its SubKrewe (held
     * for as long as the record is). The signed-in member's own changes set
     * things in motion: becoming captain syncs the SubKrewe's captains, and
     * a SubKrewe change syncs the old and new SubKrewe.
     */
    async applyMembers(changes: LiveChange<MemberFirestoreData>[]) {
      const ownPath = `members/${useCurrentUser().value?.uid}`
      const converted = await Promise.all(changes.map(async ({ path, data }) => {
        const member = this.convertToMember(path.slice('members/'.length), data!, false)
        subkrewePathOf.set(path, data!.subkrewe?.path ?? null)
        const subkrewe = await this.resolveSubkreweForMember(data!.subkrewe)
        return { path, member, subkrewe }
      }))

      const previousOwn = this.members[ownPath]
      const previousType = previousOwn?._lastSyncedType
      const previousSubkreweId = previousOwn?.subkrewe?.id
      this.$patch((state) => {
        for (const { path, member, subkrewe } of converted) {
          member.subkrewe = subkrewe
          if (path === ownPath) member._lastSyncedType = member.member_type
          state.members[path] = member
        }
      })

      const own = converted.find(({ path }) => path === ownPath)
      if (!own) return
      const member = own.member
      if (previousType && previousType !== member.member_type && member.member_type === 'captain' && member.subkrewe) {
        useSubKrewesStore().ensureCaptainSynchronized(member.subkrewe, member).catch((error) => {
          console.error('[Members] Error synchronizing captain relationship:', error)
        })
      }
      if (previousOwn && previousSubkreweId !== member.subkrewe?.id) {
        this.syncSubkreweRelationship(previousOwn, member.subkrewe!).catch((error) => {
          console.error('[Members] Error syncing subkrewe relationship:', error)
        })
      }
    },

    evictMembers(paths: string[]) {
      this.$patch((state) => {
        for (const path of paths) {
          subkrewePathOf.delete(path)
          // A captain's profile fills in again once their record is gone
          const profile = captainProfiles.get(path)
          if (profile) state.members[path] = profile
          else delete state.members[path]
        }
      })
    },

    /**
     * The Firestore update for just the fields in `updates`. Updates used to
     * write the whole member from the store's copy, which put back whatever
     * that copy held: an admin page open since the morning wiped a payment
     * the Stripe webhook had recorded since, and reverted roles, SubKrewe or
     * children. Purchases are written per field (`purchases.2027.membership`)
     * for the same reason.
     */
    toFirestoreUpdates(updates: Partial<Member>): Record<string, unknown> {
      const db = getFirestore()
      const out: Record<string, unknown> = { updated_at: serverTimestamp() }
      for (const [key, value] of Object.entries(updates)) {
        if (key === 'purchases') {
          for (const [year, yearUpdates] of Object.entries((value ?? {}) as Record<string, unknown>)) {
            if (yearUpdates === null) {
              out[`purchases.${year}`] = deleteField()
              continue
            }
            for (const [field, fieldValue] of Object.entries((yearUpdates ?? {}) as Record<string, unknown>)) {
              out[`purchases.${year}.${field}`] = fieldValue === undefined ? deleteField() : fieldValue
            }
          }
        }
        else if (key === 'subkrewe') {
          const subkrewe = value as { id?: string } | null | undefined
          out.subkrewe = subkrewe?.id ? doc(db, 'subkrewes', subkrewe.id) : deleteField()
        }
        else if (MEMBER_WRITABLE_FIELDS.includes(key)) {
          out[key] = value === undefined ? deleteField() : value
        }
      }
      return out
    },

    /**
     * Update a member with new properties
     * Handles both Firestore updates and local state
     */
    async updateMember(member: Member, updates: Partial<Member>): Promise<boolean> {
      try {
        if (!member || !member.id) {
          console.error('Cannot update member: invalid member object or missing ID')
          return false
        }

        // Comp product grants (Mark as Paid, VIP, parking, children tickets) all
        // flow through a `purchases` payload. Accounts not permitted to award
        // comps get a successful no-op here rather than an error.
        if (updates.purchases && !canAwardComps(useCurrentUser().value)) {
          return true
        }

        // Defense-in-depth: also no-op an attempt to grant admin via a generic
        // member update (the dedicated path is toggleAdminStatus).
        if (updates.roles?.admin === true && !canGrantAdmin(useCurrentUser().value)) {
          return true
        }

        // Create a deep clone of the updates to avoid modifying the original object
        // and sanitize string fields (trim leading/trailing whitespace)
        const updatesClone = deepTrim({ ...updates })

        // Special handling for purchases to ensure we merge rather than replace
        if (updatesClone.purchases && member.purchases) {
          // Extract purchases from updates and remove from the clone
          const { purchases, ...otherUpdates } = updatesClone

          // Create merged purchases by combining existing and new purchases
          const mergedPurchases = {
            ...member.purchases,
          }

          // Merge per year
          if (purchases) {
            Object.keys(purchases).forEach((year) => {
              // Create a base object with required properties and default values
              const baseDefaults = {
                hasMembership: false,
                membershipPurchasedAt: null,
                hasVip: false,
                vipPurchasedAt: null,
                hasParking: false,
                orderIds: [],
              }

              mergedPurchases[year] = {
                ...baseDefaults,
                ...(member.purchases?.[year] || {}),
                ...(purchases[year] || {}),
              }
            })
          }

          // Create updatedMember with merged purchases
          const updatedMember = deepTrim({
            ...member,
            ...otherUpdates,
            purchases: mergedPurchases,
          })

          const memberPath = `members/${member.id}`
          const db = getFirestore()
          const memberRef = doc(db, 'members', member.id)

          // Only the changed fields; the merge above is for local state
          await updateDoc(memberRef, this.toFirestoreUpdates(updatesClone))

          // Sync name/email changes to Firebase Auth (IDP)
          await this.syncAuthProfileForUpdate(member, otherUpdates)

          // Then update local state using $patch
          this.$patch((state) => {
            state.members[memberPath] = {
              ...updatedMember,
              updated_at: new Date(),
            }
          })

          // Handle subkrewe relationship synchronization
          if ('subkrewe' in updatesClone) {
            await this.syncSubkreweRelationship(member, updatedMember.subkrewe ?? null)
          }

          return true
        }
        else {
          // Default behavior when no purchases are being updated
          const updatedMember = deepTrim({ ...member, ...updates })
          const memberPath = `members/${member.id}`
          const db = getFirestore()
          const memberRef = doc(db, 'members', member.id)

          // Only the changed fields, never the whole (possibly stale) member
          await updateDoc(memberRef, this.toFirestoreUpdates(updatesClone))
          // Sync name/email changes to Firebase Auth (IDP)
          await this.syncAuthProfileForUpdate(member, updates)

          // Then update local state using $patch
          this.$patch((state) => {
            state.members[memberPath] = {
              ...updatedMember,
              updated_at: new Date(),
            }
          })

          // Handle subkrewe relationship synchronization
          if ('subkrewe' in updates) {
            await this.syncSubkreweRelationship(member, updatedMember.subkrewe ?? null)
          }

          return true
        }
      }
      catch (error: unknown) {
        const firebaseError = error as { code?: string, message?: string }
        console.error('Error updating member:', error)
        console.error('Firebase error code:', firebaseError.code)
        console.error('Firebase error message:', firebaseError.message)
        console.error('Member ID:', member.id)
        console.error('Updates attempted:', JSON.stringify(updates, null, 2))
        return false
      }
    },

    /**
     * Sync name/email changes to the Firebase Auth (IDP) record.
     * Only sends fields that actually changed. Failures are logged but do not
     * block the Firestore/local update.
     */
    async syncAuthProfileForUpdate(member: Member, updates: Partial<Member>): Promise<void> {
      const body: { userId: string, displayName?: string, email?: string } = { userId: member.id }
      if (updates.name && updates.name !== member.name) body.displayName = updates.name
      if (updates.email && updates.email !== member.email) body.email = updates.email
      if (body.displayName === undefined && body.email === undefined) return

      try {
        await withTokenRetry(async () => {
          return await $fetch('/api/members/update-auth-profile', {
            method: 'POST',
            body,
          })
        })
      }
      catch (error) {
        console.error('Error updating Firebase Auth profile:', error)
        // Continue with store update even if Auth update fails
      }
    },

    /**
     * Helper method to synchronize subkrewe relationships when a member's subkrewe changes
     */
    async syncSubkreweRelationship(member: Member, newSubkrewe: SubKrewe | null): Promise<void> {
      const subkrewesStore = useSubKrewesStore()
      const oldSubkrewe = member.subkrewe

      // Only proceed if there's an actual change
      if (oldSubkrewe?.id === newSubkrewe?.id) return

      // Case 1: Member was in a subkrewe and is now removed or moved
      if (oldSubkrewe) {
        subkrewesStore.$patch((state) => {
          const oldSubkrewePath = `subkrewes/${oldSubkrewe.id}`
          if (state.subkrewes[oldSubkrewePath]?.members) {
            state.subkrewes[oldSubkrewePath].members
              = state.subkrewes[oldSubkrewePath].members.filter(m => m.id !== member.id)
          }
        })
      }

      // Case 2: Member is being added to a new subkrewe
      if (newSubkrewe) {
        const memberPath = `members/${member.id}`
        const currentMember = this.members[memberPath]

        subkrewesStore.$patch((state) => {
          const newSubkrewePath = `subkrewes/${newSubkrewe.id}`
          if (state.subkrewes[newSubkrewePath]) {
            if (!state.subkrewes[newSubkrewePath].members) {
              state.subkrewes[newSubkrewePath].members = []
            }

            // Remove old reference if exists
            state.subkrewes[newSubkrewePath].members
              = state.subkrewes[newSubkrewePath].members.filter(m => m.id !== member.id)

            // Add updated reference. Skipped when the member is not in the
            // store, rather than pushing `undefined` into the array.
            if (currentMember) {
              state.subkrewes[newSubkrewePath].members.push(currentMember)
            }
          }
        })
      }
    },

    /**
     * Change a member's subkrewe
     * Handles bidirectional relationship
     * @param suppressNotifications If true, no notifications will be sent (useful when called from tasks)
     * @param actingUserId Optional ID of the user performing the action (for notifications)
     */
    async changeMemberSubKrewe(
      member: Member,
      newSubKrewe: SubKrewe | null,
    ): Promise<boolean> {
      try {
        if (!member || !member.id) {
          console.error('Cannot change subkrewe: invalid member')
          return false
        }

        // Block captains from changing subkrewes directly
        if (member.member_type === 'captain' && member.subkrewe && newSubKrewe) {
          console.error('Cannot change captain subkrewe directly')
          return false
        }

        // Only the SubKrewe changes. Captains add and remove members through
        // here, and firestore.rules lets them touch nothing else on another
        // member; a whole-document write from memory would also delete fields
        // that were never loaded.
        const db = getFirestore()
        const memberRef = doc(db, 'members', member.id)
        const joiningSelf = !!newSubKrewe && member.id === useCurrentUser().value?.uid
        if (joiningSelf && !isDirectlyJoinable(newSubKrewe)) {
          // Closed, by-referral, sponsored or hidden-band: the rules don't let
          // a member write it on their own record; the server checks the
          // pre-approval and writes it
          await $fetch('/api/subkrewes/join', { method: 'POST', body: { subkreweId: newSubKrewe!.id } })
        }
        else {
          await updateDoc(memberRef, {
            subkrewe: newSubKrewe ? doc(db, 'subkrewes', newSubKrewe.id) : deleteField(),
            updated_at: serverTimestamp(),
          })
        }

        // Update in-memory state using $patch
        this.$patch((state) => {
          const memberPath = `members/${member.id}`
          state.members[memberPath] = {
            ...state.members[memberPath],
            subkrewe: newSubKrewe,
            updated_at: new Date(),
          } as Member
        })

        // Handle the subkrewes-side relationship
        await this.syncSubkreweRelationship(member, newSubKrewe)

        // Remove notification logic - this is now handled exclusively in removeMemberFromSubKrewe
        // This eliminates duplicate notifications when removing members

        return true
      }
      catch (error) {
        console.error(`Error changing member ${member.id} subkrewe:`, error)
        return false
      }
    },

    /**
     * Promotes a member to captain and assigns them to a SubKrewe in one atomic operation
     * This ensures we don't have captains without SubKrewes or vice versa
     */
    async promoteToCaptain(member: Member, subkrewe: SubKrewe): Promise<boolean> {
      try {
        // Block member from having multiple operations
        this.loading = true

        const db = getFirestore()
        const memberRef = doc(db, 'members', member.id)
        const memberPath = `members/${member.id}`
        const subkrewesStore = useSubKrewesStore()

        // IMPORTANT: If member is already in a subkrewe, remove them first
        if (member.subkrewe) {
          const oldSubkrewe = member.subkrewe

          // Remove member from old subkrewe in store state
          subkrewesStore.$patch((state) => {
            const oldSubkrewePath = `subkrewes/${oldSubkrewe.id}`
            if (state.subkrewes[oldSubkrewePath]?.members) {
              state.subkrewes[oldSubkrewePath].members
                = state.subkrewes[oldSubkrewePath].members.filter(m => m.id !== member.id)
            }
          })

          // Update Firestore to remove member from old subkrewe
          const oldSubkreweRef = doc(db, 'subkrewes', oldSubkrewe.id)
          const oldSubkreweMembers = oldSubkrewe.members?.filter(m => m.id !== member.id) || []
          await updateDoc(oldSubkreweRef, {
            members: oldSubkreweMembers.map(m => doc(db, 'members', m.id)),
            updated_at: serverTimestamp(),
          })
        }

        // 1. Update the member type to captain in Firestore
        await updateDoc(memberRef, {
          member_type: 'captain',
          // Remove any existing subkrewe reference (captain->subkrewe relationship is maintained by the subkrewe)
          subkrewe: deleteField(),
          updated_at: serverTimestamp(),
        })

        // 2. Update local state
        this.$patch((state) => {
          state.members[memberPath] = {
            ...state.members[memberPath],
            member_type: 'captain',
            subkrewe: null, // Clear subkrewe reference in member
            updated_at: new Date(),
          } as Member
        })

        // 3. Assign the member as captain of the SubKrewe
        const updatedMember = this.members[memberPath] // Use the updated member from the store
        if (!updatedMember) {
          console.error(`Cannot assign captain: member ${member.id} is not in the store`)
          return false
        }

        const success = await subkrewesStore.assignCaptainToSubKrewe(subkrewe, updatedMember)

        if (!success) {
          // Roll back the member type change if SubKrewe assignment failed
          await updateDoc(memberRef, {
            member_type: member.member_type || 'member',
            updated_at: serverTimestamp(),
          })

          this.$patch((state) => {
            state.members[memberPath] = {
              ...state.members[memberPath],
              member_type: member.member_type || 'member',
              updated_at: new Date(),
            } as Member
          })

          return false
        }

        return true
      }
      catch (error) {
        console.error('Error promoting to captain:', error)
        return false
      }
      finally {
        this.loading = false
      }
    },

    /**
     * Change a member's type (member, redshirt, captain)
     * Handles all state transitions and relationship updates
     * Updated to handle multiple captains
     */
    async changeMemberType(member: Member, newType: string): Promise<boolean> {
      // No change needed if type is the same
      if (member.member_type === newType) return true

      try {
        const db = getFirestore()
        const memberRef = doc(db, 'members', member.id)
        const memberPath = `members/${member.id}`
        const subkrewesStore = useSubKrewesStore()

        // STATE TRANSITION: Regular Member → Captain
        if (newType === 'captain') {
          // Ensure member is no longer part of any subkrewe
          if (member.subkrewe) {
            const oldSubkrewe = member.subkrewe

            // Update Firestore: make captain and remove subkrewe
            await updateDoc(memberRef, {
              member_type: 'captain',
              subkrewe: deleteField(),
              updated_at: serverTimestamp(),
            })

            // Update both stores atomically
            this.$patch((state) => {
              state.members[memberPath] = {
                ...state.members[memberPath],
                member_type: 'captain',
                subkrewe: null,
                updated_at: new Date(),
              } as Member
            })

            subkrewesStore.$patch((state) => {
              const oldSubkrewePath = `subkrewes/${oldSubkrewe.id}`
              if (state.subkrewes[oldSubkrewePath]?.members) {
                state.subkrewes[oldSubkrewePath].members
                  = state.subkrewes[oldSubkrewePath].members.filter(m => m.id !== member.id)
              }
            })
          }
          else {
            // Simple case: just update member type
            await updateDoc(memberRef, {
              member_type: 'captain',
              updated_at: serverTimestamp(),
            })

            this.$patch((state) => {
              state.members[memberPath] = {
                ...state.members[memberPath],
                member_type: 'captain',
                updated_at: new Date(),
              } as Member
            })
          }

          return true
        }

        // STATE TRANSITION: Captain → Regular Member
        if (member.member_type === 'captain' && newType !== 'captain') {
          const oldSubkrewe = member.subkrewe

          // Take them off the SubKrewe's captains first, and stop if that
          // fails. A captain the SubKrewe doesn't list has nothing to remove.
          if (oldSubkrewe?.captains?.some(c => c.id === member.id)) {
            const removed = await subkrewesStore.removeCaptainFromSubKrewe(oldSubkrewe, member, newType === 'member')
            if (!removed) {
              console.error(`Could not remove captain ${member.id} from SubKrewe ${oldSubkrewe.id}`)
              return false
            }
          }

          // Update the member type
          await updateDoc(memberRef, {
            member_type: newType,
            updated_at: serverTimestamp(),
          })

          this.$patch((state) => {
            state.members[memberPath] = {
              ...state.members[memberPath],
              member_type: newType,
              updated_at: new Date(),
            } as Member
          })

          return true
        }

        // Simple type change (member ↔ redshirt)
        await updateDoc(memberRef, {
          member_type: newType,
          updated_at: serverTimestamp(),
        })

        this.$patch((state) => {
          state.members[memberPath] = {
            ...state.members[memberPath],
            member_type: newType,
            updated_at: new Date(),
          } as Member
        })

        return true
      }
      catch (error) {
        console.error('Error changing member type:', error)
        return false
      }
    }, /**
     * Toggle a member's admin status
     * @param member The member to update
     * @param isAdmin New admin status
     * @returns Promise<boolean> indicating success
     */
    async toggleAdminStatus(member: Member, isAdmin: boolean): Promise<boolean> {
      try {
        if (!member?.id) {
          console.error('Cannot toggle admin status: invalid member object')
          return false
        }

        // Changing another member's admin status is a privileged action; no-op
        // (but appear to succeed) for accounts not permitted to manage it. This
        // covers both granting and revoking admin.
        if (!canGrantAdmin(useCurrentUser().value)) {
          return true
        }

        // Get Firestore instance - this is properly mocked in tests
        const db = getFirestore()
        const memberRef = doc(db, 'members', member.id)
        const memberPath = `members/${member.id}`

        // Create roles object if it doesn't exist
        const roles = member.roles || {}
        roles.admin = isAdmin

        // Update Firestore
        await updateDoc(memberRef, {
          roles,
          updated_at: serverTimestamp(),
        })

        // Update local state
        this.$patch((state) => {
          if (!state.members[memberPath]) {
            state.members[memberPath] = member
          }
          if (!state.members[memberPath].roles) {
            state.members[memberPath].roles = {}
          }
          state.members[memberPath].roles.admin = isAdmin
          state.members[memberPath].updated_at = new Date()
        })

        return true
      }
      catch (error) {
        console.error(`Error toggling admin status for member ${member.id}:`, error)
        return false
      }
    },

    /**
     * Toggle a member's band admin status
     * @param member The member to update
     * @param isBandAdmin New band admin status
     * @returns Promise<boolean> indicating success
     */
    async toggleBandAdminStatus(member: Member, isBandAdmin: boolean): Promise<boolean> {
      try {
        if (!member?.id) {
          console.error('Cannot toggle band admin status: invalid member object')
          return false
        }

        // Get Firestore instance
        const db = getFirestore()
        const memberRef = doc(db, 'members', member.id)
        const memberPath = `members/${member.id}`

        // Create roles object if it doesn't exist
        const roles = member.roles || {}
        roles.band_admin = isBandAdmin

        // Update Firestore
        await updateDoc(memberRef, {
          roles,
          updated_at: serverTimestamp(),
        })

        // Update local state
        this.$patch((state) => {
          if (!state.members[memberPath]) {
            state.members[memberPath] = member
          }
          if (!state.members[memberPath].roles) {
            state.members[memberPath].roles = {}
          }
          state.members[memberPath].roles.band_admin = isBandAdmin
          state.members[memberPath].updated_at = new Date()
        })

        return true
      }
      catch (error) {
        console.error(`Error toggling band admin status for member ${member.id}:`, error)
        return false
      }
    },

    /**
     * Toggle a member's redshirt admin status
     * @param member The member to update
     * @param isRedshirtAdmin New redshirt admin status
     * @returns Promise<boolean> indicating success
     */
    async toggleRedshirtAdminStatus(member: Member, isRedshirtAdmin: boolean): Promise<boolean> {
      try {
        if (!member?.id) {
          console.error('Cannot toggle redshirt admin status: invalid member object')
          return false
        }

        const db = getFirestore()
        const memberRef = doc(db, 'members', member.id)
        const memberPath = `members/${member.id}`

        const roles = member.roles || {}
        roles.redshirt_admin = isRedshirtAdmin

        await updateDoc(memberRef, {
          roles,
          updated_at: serverTimestamp(),
        })

        this.$patch((state) => {
          if (!state.members[memberPath]) {
            state.members[memberPath] = member
          }
          if (!state.members[memberPath].roles) {
            state.members[memberPath].roles = {}
          }
          state.members[memberPath].roles.redshirt_admin = isRedshirtAdmin
          state.members[memberPath].updated_at = new Date()
        })

        return true
      }
      catch (error) {
        console.error(`Error toggling redshirt admin status for member ${member.id}:`, error)
        return false
      }
    },

    /**
     * Toggle a member's check-in volunteer status
     * @param member The member to update
     * @param isCheckinVolunteer New check-in volunteer status
     * @returns Promise<boolean> indicating success
     */
    async toggleCheckinVolunteerStatus(member: Member, isCheckinVolunteer: boolean): Promise<boolean> {
      try {
        if (!member?.id) {
          console.error('Cannot toggle check-in volunteer status: invalid member object')
          return false
        }

        const db = getFirestore()
        const memberRef = doc(db, 'members', member.id)
        const memberPath = `members/${member.id}`

        const roles = member.roles || {}
        roles.checkinVolunteer = isCheckinVolunteer

        await updateDoc(memberRef, {
          roles,
          updated_at: serverTimestamp(),
        })

        this.$patch((state) => {
          if (!state.members[memberPath]) {
            state.members[memberPath] = member
          }
          if (!state.members[memberPath].roles) {
            state.members[memberPath].roles = {}
          }
          state.members[memberPath].roles.checkinVolunteer = isCheckinVolunteer
          state.members[memberPath].updated_at = new Date()
        })

        return true
      }
      catch (error) {
        console.error(`Error toggling check-in volunteer status for member ${member.id}:`, error)
        return false
      }
    },

    /**
    * Toggle a member's beta tester status
    * @param member The member to update
    * @param isBetaTester New beta tester status
    * @returns Promise<boolean> indicating success
    */
    async toggleBetaTesterStatus(member: Member, isBetaTester: boolean): Promise<boolean> {
      try {
        if (!member?.id) {
          console.error('Cannot toggle beta tester status: invalid member object')
          return false
        }

        const db = getFirestore()
        const memberRef = doc(db, 'members', member.id)
        const memberPath = `members/${member.id}`

        await updateDoc(memberRef, {
          beta_tester: isBetaTester,
          updated_at: serverTimestamp(),
        })

        this.$patch((state) => {
          if (!state.members[memberPath]) {
            state.members[memberPath] = member
          }
          state.members[memberPath].beta_tester = isBetaTester
          state.members[memberPath].updated_at = new Date()
        })

        return true
      }
      catch (error) {
        console.error(`Error toggling beta tester status for member ${member.id}:`, error)
        return false
      }
    },

    /**
    * Toggle a member's banned status
    * @param member The member to update
    * @param isBanned New banned status
    * @param banReason Reason for banning (required when banning, ignored when unbanning)
    * @returns Promise<boolean> indicating success
    */
    async toggleBanStatus(member: Member, isBanned: boolean, banReason?: string): Promise<boolean> {
      try {
        if (!member?.id) {
          console.error('Cannot toggle ban status: invalid member object')
          return false
        }

        // Validate that a reason is provided when banning
        if (isBanned && !banReason) {
          console.error('Ban reason is required when banning a member')
          return false
        }

        // Get Firestore instance
        const db = getFirestore()
        const memberRef = doc(db, 'members', member.id)
        const memberPath = `members/${member.id}`

        // Prepare update data
        const updateData: Partial<MemberFirestoreData> = {
          banned: isBanned,
          updated_at: serverTimestamp(),
        }

        // Add ban reason and timestamp if banning
        if (isBanned) {
          updateData.banned_reason = banReason
          updateData.banned_at = serverTimestamp()
        }
        else {
          // Remove ban reason and timestamp when unbanning
          updateData.banned_reason = deleteField()
          updateData.banned_at = deleteField()
        }

        // If banning a member who belongs to a SubKrewe, remove them from the SubKrewe
        if (isBanned && member.subkrewe) {
          // Pass the current user ID and 'banned' as the reason
          const currentUserId = useCurrentUser().value?.uid
          if (!await removeFromOwnSubKrewe(member, currentUserId, 'banned')) {
            console.error(`Could not remove ${member.id} from their SubKrewe; not banning`)
            return false
          }

          // Since we're removing from SubKrewe, we need to update the subkrewe reference
          updateData.subkrewe = deleteField()
        }

        // First call our API to disable/enable the user in Firebase Auth
        try {
          await withTokenRetry(async () => {
            // Use $fetch directly instead of useFetch for better post-mount behavior
            return await $fetch('/api/admin/toggle-user-ban', {
              method: 'POST',
              body: {
                userId: member.id,
                banned: isBanned,
                reason: banReason,
              },
            })
          })
        }
        catch (error) {
          console.error('Error calling ban API:', error)
          return false
        }

        // Then update Firestore
        await updateDoc(memberRef, updateData)

        // Update local state
        this.$patch((state) => {
          if (!state.members[memberPath]) {
            state.members[memberPath] = member
          }

          state.members[memberPath].banned = isBanned

          if (isBanned) {
            state.members[memberPath].banned_reason = banReason
            state.members[memberPath].banned_at = new Date()
          }
          else {
            delete state.members[memberPath].banned_reason
            delete state.members[memberPath].banned_at
          }

          // If banning, also remove SubKrewe reference in local state
          if (isBanned && member.subkrewe) {
            state.members[memberPath].subkrewe = null
          }

          state.members[memberPath].updated_at = new Date()
        })

        return true
      }
      catch (error) {
        console.error(`Error toggling ban status for member ${member.id}:`, error)
        return false
      }
    },

    /**
 * Toggle a member's lifetime status
 * @param member The member to update
 * @param isLifetime New lifetime status
 * @param reason Optional reason for granting lifetime status
 * @returns Promise<boolean> indicating success
 */
    async toggleLifetimeStatus(member: Member, isLifetime: boolean, reason?: string): Promise<boolean> {
      try {
        if (!member?.id) {
          throw new Error('Invalid member data')
        }

        // Validate that a reason is provided when granting lifetime status
        if (isLifetime && !reason) {
          throw new Error('A reason is required when granting lifetime status')
        }

        // Granting lifetime is a comp award; no-op for accounts not permitted.
        if (isLifetime && !canAwardComps(useCurrentUser().value)) {
          return true
        }

        // Get Firestore instance
        const db = getFirestore()
        const memberRef = doc(db, 'members', member.id)
        const memberPath = `members/${member.id}`

        // Prepare update data
        const updateData: Partial<MemberFirestoreData> = {
          lifetime: isLifetime,
          updated_at: serverTimestamp(),
        }

        // Add lifetime reason and timestamp if granting lifetime status
        if (isLifetime) {
          updateData.lifetime_reason = reason
          updateData.lifetime_since = serverTimestamp()
        }
        else {
          // If removing lifetime status, remove reason and timestamp
          updateData.lifetime_reason = deleteField()
          updateData.lifetime_since = deleteField()
        }

        // Update Firestore
        await updateDoc(memberRef, updateData)

        // Update local state
        this.$patch((state) => {
          if (state.members[memberPath]) {
            state.members[memberPath] = {
              ...state.members[memberPath],
              lifetime: isLifetime,
              lifetime_reason: isLifetime ? reason : undefined,
              lifetime_since: isLifetime ? new Date() : undefined,
            }
          }
        })

        return true
      }
      catch (error) {
        console.error(`Error toggling lifetime status for member ${member.id}:`, error)
        return false
      }
    },

    /**
     * Toggle a member's Golden Wookiee award status
     */
    async toggleGoldenWookieeStatus(member: Member, isGoldenWookiee: boolean, year?: number, reason?: string): Promise<boolean> {
      try {
        if (!member?.id) {
          throw new Error('Invalid member data')
        }

        if (isGoldenWookiee && (!year || !reason)) {
          throw new Error('Year and reason are required when granting a Golden Wookiee')
        }

        // Granting a Golden Wookiee is a comp award; no-op when not permitted.
        if (isGoldenWookiee && !canAwardComps(useCurrentUser().value)) {
          return true
        }

        const db = getFirestore()
        const memberRef = doc(db, 'members', member.id)
        const memberPath = `members/${member.id}`

        const updateData: Partial<MemberFirestoreData> = {
          golden_wookiee: isGoldenWookiee,
          updated_at: serverTimestamp(),
        }

        if (isGoldenWookiee) {
          updateData.golden_wookiee_year = year
          updateData.golden_wookiee_reason = reason
        }
        else {
          updateData.golden_wookiee_year = deleteField()
          updateData.golden_wookiee_reason = deleteField()
        }

        await updateDoc(memberRef, updateData)

        this.$patch((state) => {
          if (state.members[memberPath]) {
            state.members[memberPath] = {
              ...state.members[memberPath],
              golden_wookiee: isGoldenWookiee,
              golden_wookiee_year: isGoldenWookiee ? year : undefined,
              golden_wookiee_reason: isGoldenWookiee ? reason : undefined,
            }
          }
        })

        return true
      }
      catch (error) {
        console.error(`Error toggling Golden Wookiee status for member ${member.id}:`, error)
        return false
      }
    },

    /**
     * Hold the signed-in member's own record live
     * @returns The release
     */
    subscribeToCurrentMember(): (() => void) | null {
      const user = useCurrentUser().value
      if (!user?.uid) {
        console.warn('[Members] Cannot subscribe to current member: No authenticated user')
        return null
      }
      return holdLive(this.memberSpec(user.uid)).release
    },

    /**
     * Add a child to a member
     */
    async addChildToMember(member: Member, child: { id: string, firstName: string, lastName: string, birthday: unknown }): Promise<boolean> {
      try {
        if (!member?.id) {
          console.error('Cannot add child: invalid member object')
          return false
        }

        // Block redshirts from child mutations
        if (isRedshirt(member)) {
          console.warn(`[Members] Blocked addChildToMember for redshirt ${member.id}`)
          return false
        }

        const db = getFirestore()
        const memberRef = doc(db, 'members', member.id)

        // Extract ID from child and create rest of data
        const { id, ...childData } = child
        const childToAdd = {
          ...childData,
          birthday: typeof childData.birthday === 'string' ? childData.birthday : new Date(),
          created_at: new Date(),
        }

        // Update just this child's entry in the map
        await updateDoc(memberRef, {
          [`children.${id}`]: childToAdd,
          updated_at: serverTimestamp(),
        })

        // Update local state
        this.$patch((state) => {
          const memberPath = `members/${member.id}`
          if (state.members[memberPath]) {
            if (!state.members[memberPath].children) {
              state.members[memberPath].children = {}
            }
            state.members[memberPath].children[id] = childToAdd
            state.members[memberPath].updated_at = new Date()
          }
        })

        return true
      }
      catch (error) {
        console.error(`Error adding child to member ${member.id}:`, error)
        return false
      }
    },

    /**
     * Update a child's information
     */
    async updateChildForMember(member: Member, child: { id: string, firstName: string, lastName: string, birthday: unknown }): Promise<boolean> {
      try {
        if (!member?.id || !child.id) {
          console.error('Cannot update child: invalid member or child object')
          return false
        }

        // Block redshirts from child mutations
        if (isRedshirt(member)) {
          console.warn(`[Members] Blocked updateChildForMember for redshirt ${member.id}`)
          return false
        }

        const db = getFirestore()
        const memberRef = doc(db, 'members', member.id)

        // Extract ID from child
        const { id, ...childData } = child

        // Update just this child's entry in the map
        await updateDoc(memberRef, {
          [`children.${id}`]: {
            ...childData,
            birthday: typeof childData.birthday === 'string' ? childData.birthday : new Date(),
          },
          updated_at: serverTimestamp(),
        })

        // Update local state
        this.$patch((state) => {
          const memberPath = `members/${member.id}`
          if (state.members[memberPath]) {
            if (!state.members[memberPath].children) {
              state.members[memberPath].children = {}
            }
            state.members[memberPath].children[id] = {
              ...childData,
              birthday: typeof childData.birthday === 'string' ? childData.birthday : new Date(),
            }
            state.members[memberPath].updated_at = new Date()
          }
        })

        return true
      }
      catch (error) {
        console.error(`Error updating child for member ${member.id}:`, error)
        return false
      }
    },

    /**
     * Remove a child from a member
     */
    async removeChildFromMember(member: Member, childId: string): Promise<boolean> {
      try {
        if (!member?.id) {
          console.error('Cannot remove child: invalid member object')
          return false
        }

        // Block redshirts from child mutations
        if (isRedshirt(member)) {
          console.warn(`[Members] Blocked removeChildFromMember for redshirt ${member.id}`)
          return false
        }

        const db = getFirestore()
        const memberRef = doc(db, 'members', member.id)

        // Delete this child's entry from the map
        await updateDoc(memberRef, {
          [`children.${childId}`]: deleteField(),
          updated_at: serverTimestamp(),
        })

        // Update local state
        this.$patch((state) => {
          const memberPath = `members/${member.id}`
          if (state.members[memberPath]?.children) {
            const updatedChildren = { ...state.members[memberPath].children }
            delete updatedChildren[childId]
            state.members[memberPath].children = updatedChildren
            state.members[memberPath].updated_at = new Date()
          }
        })

        return true
      }
      catch (error) {
        console.error(`Error removing child from member ${member.id}:`, error)
        return false
      }
    },

    /**
     * Delete a member completely, including their auth account
     * @param member The member to delete
     * @returns Promise<boolean> indicating success
     */
    async deleteUser(member: Member): Promise<boolean> {
      try {
        if (!member?.id) {
          console.error('Cannot delete user: invalid member object')
          return false
        }

        // Out of their SubKrewe before the auth account goes: once it's gone,
        // the delete API fails, so a delete that stopped later can't be redone
        const currentUserId = useCurrentUser().value?.uid
        if (!await removeFromOwnSubKrewe(member, currentUserId, 'deleted')) {
          console.error(`Could not remove ${member.id} from their SubKrewe; not deleting`)
          return false
        }

        // Then call our API to delete the user from Firebase Auth
        try {
          await withTokenRetry(async () => {
            return await $fetch('/api/admin/delete-user', {
              method: 'POST',
              body: {
                userId: member.id,
              },
            })
          })
        }
        catch (error) {
          console.error('Error calling delete user API:', error)
          return false
        }

        // Get Firestore instance
        const db = getFirestore()
        const memberRef = doc(db, 'members', member.id)
        const memberPath = `members/${member.id}`

        // Delete the document from Firestore
        await deleteDoc(memberRef)

        // Update local state
        this.$patch((state) => {
          delete state.members[memberPath]
        })

        return true
      }
      catch (error) {
        console.error(`Error deleting user ${member.id}:`, error)
        return false
      }
    },

    /**
     * Delete only the member document from Firestore (for self-deletion)
     * Used when a user deletes their own account - they handle auth deletion client-side
     * @param memberId The member ID to delete
     * @returns Promise<boolean> indicating success
     */
    async deleteMemberDocument(memberId: string): Promise<boolean> {
      try {
        const memberPath = `members/${memberId}`
        const member = this.members[memberPath]

        // Remove from subkrewe if applicable. The member asked to be deleted,
        // so a SubKrewe that couldn't be updated doesn't keep their record.
        if (member && !await removeFromOwnSubKrewe(member, memberId, 'deleted')) {
          console.error(`Could not remove ${memberId} from their SubKrewe; deleting the member anyway`)
        }

        // Delete the document from Firestore
        const db = getFirestore()
        const memberRef = doc(db, 'members', memberId)
        await deleteDoc(memberRef)

        // Update local state
        this.$patch((state) => {
          delete state.members[memberPath]
        })

        return true
      }
      catch (error) {
        console.error(`Error deleting member document ${memberId}:`, error)
        return false
      }
    },
  },
})
